"use client";

import { c as _c } from "react/compiler-runtime";
import { Filters } from "@/types/filters";
import { logger } from "@/utils/logger";
import { filtersToUrl, urlToFilters } from "@/utils/search-url-transform";
import { parseFiltersFromSearchParams } from "@/utils/search/parse-search-params";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useSearchPageInteraction, useSearchPageMapData } from "../providers/search";
export type ViewMode = "map" | "list";
export type SearchMode = "default" | "project";
interface UrlState {
  filters: Partial<Filters>;
  mode: SearchMode;
  view: ViewMode;
}
interface UrlStateContextType {
  state: UrlState;
  setUrlState: (newState: Partial<UrlState>, options?: {
    useReplaceState?: boolean;
  }) => void;
  setFilters: (filters: Partial<Filters>, options?: {
    useReplaceState?: boolean;
  }) => void;
  setMode: (mode: SearchMode) => void;
  setView: (view: ViewMode) => void;
}
export const UrlStateContext = createContext<UrlStateContextType | undefined>(undefined);
export function UrlStateProvider(t0) {
  const $ = _c(37);
  const {
    children,
    initialFilters
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const setStoreView = useSearchPageInteraction(_temp);
  const storeView = useSearchPageInteraction(_temp2);
  const setStoreMode = useSearchPageMapData(_temp3);
  const storeMode = useSearchPageMapData(_temp4);
  const internalUpdateRef = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const pendingStateRef = useRef(t1);
  let filters;
  let params;
  if ($[1] !== initialFilters || $[2] !== pathname || $[3] !== searchParams) {
    const slug = pathname?.split("/").pop() || "";
    params = searchParams || new URLSearchParams();
    filters = {
      ...urlToFilters(slug),
      ...parseFiltersFromSearchParams(params)
    } as Partial<Filters>;
    if (initialFilters && Object.keys(filters).length === 0) {
      filters = initialFilters;
    } else {
      filters.locations = filters.locations?.map(location => ({
        ...location,
        ...(initialFilters?.locations?.find(item => item.name === location.name) || {})
      })) || [];
    }
    $[1] = initialFilters;
    $[2] = pathname;
    $[3] = searchParams;
    $[4] = filters;
    $[5] = params;
  } else {
    filters = $[4];
    params = $[5];
  }
  const modeParam = params.get("mode");
  const mode = modeParam === "projects" ? "project" : "default";
  const viewParam = params.get("view");
  const view = viewParam === "list" ? "list" : "map";
  let t2;
  if ($[6] !== filters || $[7] !== mode || $[8] !== view) {
    t2 = {
      filters,
      mode,
      view
    };
    $[6] = filters;
    $[7] = mode;
    $[8] = view;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const urlState = t2;
  let t3;
  if ($[10] !== pathname || $[11] !== router || $[12] !== urlState) {
    t3 = (newState, t4) => {
      const options = t4 === undefined ? {} : t4;
      const {
        useReplaceState: t5
      } = options;
      const useReplaceState = t5 === undefined ? false : t5;
      pendingStateRef.current = {
        ...pendingStateRef.current,
        ...newState
      };
      const currentState = {
        ...urlState,
        ...pendingStateRef.current
      };
      const mergedFilters = newState.filters !== undefined ? newState.filters : currentState.filters;
      const mergedMode = newState.mode !== undefined ? newState.mode : currentState.mode;
      const mergedView = newState.view !== undefined ? newState.view : currentState.view;
      const {
        bounds,
        selectedLocationId,
        coordinates,
        zoom,
        ...pathFilters
      } = mergedFilters;
      const newSlug = filtersToUrl(pathFilters);
      const segments = pathname?.split("/").filter(Boolean) || [];
      const citySegment = segments[0] || "bengaluru";
      const newPath = newSlug ? `/${citySegment}/${newSlug}` : `/${citySegment}`;
      const newSearchParams = new URLSearchParams(window.location.search);
      if (mergedView === "map") {
        newSearchParams.delete("view");
      } else {
        newSearchParams.set("view", "list");
      }
      if (mergedMode === "project") {
        newSearchParams.set("mode", "projects");
      } else {
        newSearchParams.delete("mode");
      }
      if (bounds && bounds.length > 0) {
        const boundsObj = bounds[0];
        newSearchParams.set("bounds", `${boundsObj.north}-${boundsObj.east}-${boundsObj.south}-${boundsObj.west}`);
      } else {
        newSearchParams.delete("bounds");
      }
      if (selectedLocationId) {
        newSearchParams.set("selectedLocationId", selectedLocationId);
      } else {
        newSearchParams.delete("selectedLocationId");
      }
      if (coordinates) {
        newSearchParams.set("center", `${coordinates.lat}-${coordinates.lng}`);
      } else {
        newSearchParams.delete("center");
      }
      if (zoom) {
        newSearchParams.set("zoom", zoom.toString());
      } else {
        newSearchParams.delete("zoom");
      }
      const finalUrl = newSearchParams.toString() ? `${newPath}?${newSearchParams.toString()}` : newPath;
      internalUpdateRef.current = true;
      const modeOrViewChanged = newState.mode !== undefined || newState.view !== undefined;
      logger.debug("[useUrlState] setUrlState updating URL", {
        finalUrl,
        useReplaceState,
        modeOrViewChanged
      });
      if (modeOrViewChanged) {
        if (useReplaceState) {
          router.replace(finalUrl, {
            scroll: false
          });
        } else {
          router.push(finalUrl, {
            scroll: false
          });
        }
      } else {
        if (useReplaceState) {
          window.history.replaceState(null, "", finalUrl);
        } else {
          window.history.pushState(null, "", finalUrl);
        }
      }
      setTimeout(() => {
        internalUpdateRef.current = false;
        pendingStateRef.current = {};
      }, 100);
    };
    $[10] = pathname;
    $[11] = router;
    $[12] = urlState;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const setUrlState = t3;
  let t4;
  if ($[14] !== setUrlState) {
    t4 = (filters_0, options_0) => {
      setUrlState({
        filters: filters_0
      }, options_0);
    };
    $[14] = setUrlState;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const setFilters = t4;
  let t5;
  if ($[16] !== setUrlState) {
    t5 = mode_0 => {
      setUrlState({
        mode: mode_0
      });
    };
    $[16] = setUrlState;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const setMode = t5;
  let t6;
  if ($[18] !== setUrlState) {
    t6 = view_0 => {
      setUrlState({
        view: view_0
      });
    };
    $[18] = setUrlState;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const setView = t6;
  let t7;
  let t8;
  if ($[20] !== setStoreMode || $[21] !== setStoreView || $[22] !== storeMode || $[23] !== storeView || $[24] !== urlState.mode || $[25] !== urlState.view) {
    t7 = () => {
      if (urlState.view !== storeView) {
        setStoreView(urlState.view);
      }
      if (urlState.mode !== storeMode) {
        setStoreMode(urlState.mode);
      }
    };
    t8 = [urlState.view, urlState.mode, setStoreView, setStoreMode, storeView, storeMode];
    $[20] = setStoreMode;
    $[21] = setStoreView;
    $[22] = storeMode;
    $[23] = storeView;
    $[24] = urlState.mode;
    $[25] = urlState.view;
    $[26] = t7;
    $[27] = t8;
  } else {
    t7 = $[26];
    t8 = $[27];
  }
  useEffect(t7, t8);
  let t9;
  if ($[28] !== setFilters || $[29] !== setMode || $[30] !== setUrlState || $[31] !== setView || $[32] !== urlState) {
    t9 = {
      state: urlState,
      setUrlState,
      setFilters,
      setMode,
      setView
    };
    $[28] = setFilters;
    $[29] = setMode;
    $[30] = setUrlState;
    $[31] = setView;
    $[32] = urlState;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== children || $[35] !== t9) {
    t10 = <UrlStateContext.Provider value={t9}>{children}</UrlStateContext.Provider>;
    $[34] = children;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}
function _temp4(state_2) {
  return state_2.mode;
}
function _temp3(state_1) {
  return state_1.setMode;
}
function _temp2(state_0) {
  return state_0.view;
}
function _temp(state) {
  return state.setView;
}
export function useUrlState() {
  const context = useContext(UrlStateContext);
  if (context === undefined) {
    throw new Error("useUrlState must be used within a UrlStateProvider");
  }
  return context;
}